import { useEffect, useState } from 'react'
import { Link as RouterLink, useNavigate, useSearchParams } from 'react-router-dom'

import { Box, Button, Link, Typography } from '@mui/material'
import { useTheme } from '@mui/material/styles'
import request from 'api'
import { Loading } from 'components/ui'
import { useDateTime, useMappedTranslation } from 'hooks'
import { getConsentApiCountry, getGlobalConsentApi } from 'pages/policies/api'
import { Accordion, Action, Consent, appendLegacyParams, buildPolicySearch, getListLength, trim } from 'pages/policies/utils'
import Detail from 'pages/policies/views/detail'
import i18n, { convertLanguageCode2to3 } from 'utils/i18n'

import PrivacyIcon from '../../assets/images/privacy_icon.png'

enum PRIVACY_TYPE {
  MAJOR = 'PDP',
  MINOR = 'MPC'
}

export default function PrivacyNoticesLatest() {
  const t = useMappedTranslation()
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const theme = useTheme()
  const dateTime = useDateTime()
  const type = searchParams.get('type') === 'minor' ? PRIVACY_TYPE.MINOR : PRIVACY_TYPE.MAJOR
  const paramCountryCode = searchParams.get('countryCode') || undefined
  const paramLocale = searchParams.get('locale') || undefined
  const policySearch = buildPolicySearch(searchParams)
  const minorSearch = buildPolicySearch(searchParams, { type: 'minor' })
  const language3DigitCode = i18n.languageCode3digit.toLowerCase()
  const effectiveLanguage3DigitCode = paramLocale
    ? convertLanguageCode2to3(paramLocale.replace('_', '-').split('-')[0]) || language3DigitCode
    : language3DigitCode

  const [isLoading, setIsLoading] = useState<boolean>(true)
  const [consent, setConsent] = useState<Consent>()
  const [detail, setDetail] = useState<string>('<div/>')
  const [title, setTitle] = useState<string>('')
  const [accordions, setAccordions] = useState<Accordion[]>([])
  const [action, setAction] = useState<Action>({} as Action)

  const getDetailApi = (uri: string) => {
    const LEGACY_HOST = process.env.REACT_APP_HOST
    return request
      .get(uri)
      .then((res) => {
        if (res.status !== 200) {
          throw new Error('Failed getConsentBody')
        }

        const document = new DOMParser().parseFromString(res.data, 'text/html')
        const _title = document.getElementById('ppTitle')?.innerText || ''

        const language = paramLocale?.replace('-', '_') || i18n.language?.replace('-', '_')
        if (language) {
          document.querySelectorAll('a').forEach((tag) => {
            if (tag.href.includes(`${LEGACY_HOST}`)) {
              tag.href = appendLegacyParams(tag.href, language, paramCountryCode)
            }
          })
        }

        let _detail = trim(document.getElementById('ppContent')?.innerHTML || '')
        const accordionContainer: Accordion[] = []
        const contentLength = getListLength(document)
        for (let i = 1; i <= contentLength; i++) {
          let title = document.getElementById(`listTitle${String(i).padStart(2, '0')}`)?.innerHTML || ''
          let description = trim(document.getElementById(`listContent${String(i).padStart(2, '0')}`)?.innerHTML) || ''
          accordionContainer.push({ title, description })
        }

        setTitle(_title)
        setDetail(_detail)
        setAccordions(accordionContainer)
        setAction({ uri })
      })
      .catch(() => setDetail('<div>Failed to load</div>'))
  }

  const getGlobalDetailApi = (uri: string) => {
    const LEGACY_HOST = process.env.REACT_APP_HOST
    return request
      .get(uri)
      .then((res) => {
        if (res.status !== 200) {
          throw new Error('Failed getConsentBody')
        }

        const document = new DOMParser().parseFromString(res.data, 'text/html')
        const _title = document.getElementById('ppTitle')?.innerText || t('iam.02.Agree.Terms.PP.title.01')
        const language = paramLocale?.replace('-', '_') || i18n.language?.replace('-', '_')

        if (language) {
          document.querySelectorAll('a').forEach((tag) => {
            if (tag.href.includes(`${LEGACY_HOST}`)) {
              tag.href = appendLegacyParams(tag.href, language, paramCountryCode)
            }
          })
        }

        setTitle(_title)

        const body = document.getElementsByTagName('body').item(0)?.innerHTML
        if (body !== undefined) {
          const sanitized = body
            .replace(
              'body { word-wrap:break-word; word-break:keep-all; padding:1.5em; }',
              'body { word-wrap:break-word; word-break:keep-all; }'
            )
            .replace('Samsung Hesabı Aydınlatma Metni', '')
            .replace('Samsung account Privacy Notice', '')
          setDetail(sanitized)
        }
        setAccordions([])
      })
      .catch(() => setDetail('<div>Failed to load</div>'))
  }

  const renderActions = () => {
    switch (type) {
      case PRIVACY_TYPE.MAJOR:
        return (
          <>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                pt: { xs: '52px', md: '72px' }
              }}
            >
              <Typography
                className='off'
                fontSize='13px'
                lineHeight='16px'
                fontWeight='700'
                color={theme.palette.mode === 'dark' ? '#999999' : '#636363'}
              >
                {t('iam.01.Common.Txt.Effective.date', { 0: dateTime(Number(consent?.publish), { format: 'noticeDate' }) })}
              </Typography>
              <Box
                sx={{
                  marginTop: '12px'
                }}
              >
                <Link
                  to={`/policies/privacy-notices${policySearch}`}
                  component={RouterLink}
                  sx={{
                    padding: '7px 12px',
                    fontSize: '13px',
                    lineHeight: '19px',
                    minHeight: '0',
                    textTransform: 'none',
                    color: 'text.primary',
                    textDecoration: 'none',
                    border: `0.5px solid #636363`,
                    borderRadius: '24px',
                    textAlign: 'center',
                    minWidth: { xs: '152px', md: '198px' },
                    display: 'block',
                    height: '33px'
                  }}
                  data-testid='LinkB7E5A3CB'
                >
                  {t('iam.01.Common.Notice.detail.list.01')}
                </Link>
              </Box>
              <Box sx={{ marginTop: '24px', height: '24px' }}>
                <Button
                  variant='text'
                  onClick={() => window.open(consent?.uri || '', '_blank')}
                  sx={{ width: '100%', height: '24px', minHeight: '24px', padding: 0 }}
                  data-testid='ButtonEF210ADC'
                >
                  <Typography
                    fontSize='15px'
                    fontWeight='700'
                    lineHeight='24px'
                    color={theme.palette.mode === 'dark' ? '#A9A9A9' : '#505050'}
                    sx={{
                      textDecoration: 'underline'
                    }}
                  >
                    {t('iam.01.Common.Text.Trouble.reading.our.Privacy.Notice')}
                  </Typography>
                </Button>
              </Box>
            </Box>
          </>
        )
      case PRIVACY_TYPE.MINOR:
        return (
          <Box sx={{ marginTop: '24px' }}>
            <Typography
              tabIndex={0}
              onClick={() => window.open(action.uri, '_blank')}
              color={theme.palette.mode === 'dark' ? '#A9A9A9' : '#505050'}
              sx={{
                cursor: 'pointer',
                width: '100%',
                textDecoration: 'underline',
                fontSize: '15px',
                fontWeight: 700,
                lineHeight: '24px'
              }}
              data-testid='TypographyE04249BA'
            >
              {t('iam.01.Common.Text.Trouble.reading.our.Privacy.Notice')}
            </Typography>
          </Box>
        )
    }
  }

  useEffect(() => {
    setIsLoading(true)

    if (type === PRIVACY_TYPE.MAJOR) {
      getGlobalConsentApi('PN', false, { overrides: { countryCode: paramCountryCode, locale: paramLocale } })
        .then((res) => {
          if (res.status !== 200) {
            throw new Error('Failed getConsentApi')
          } else {
            const data = res.data[0] as Consent
            setConsent(data)
            return data
          }
        })
        .then((data) => {
          return getGlobalDetailApi(data.uri)
        })
        .catch(() => {
          setConsent(undefined)
        })
        .finally(() => {
          setIsLoading(false)
        })
    } else {
      getConsentApiCountry(type.toString(), false, 'TUR', effectiveLanguage3DigitCode)
        .then((res) => {
          if (res.status !== 200) {
            throw new Error('Failed getConsentApi')
          } else {
            const data = res.data[0] as Consent
            setConsent(data)
            return data
          }
        })
        .then((data) => {
          return getDetailApi(data.uri)
        })
        .catch(() => {
          setConsent(undefined)
        })
        .finally(() => {
          setIsLoading(false)
        })
    }
  }, [type, paramCountryCode, paramLocale, effectiveLanguage3DigitCode])

  return (
    <Box sx={{ width: '100%', height: '100%', mt: { xs: '-54px', lg: '-12px' } }}>
      {isLoading ? (
        <Loading fullScreen />
      ) : (
        <Box sx={{ display: 'flex', justifyContent: 'center', padding: { xs: '0 24px', lg: '0' } }}>
          <Box sx={{ width: { xs: '100%', lg: '1040px' } }}>
            <Detail
              title={title}
              titleIcon={PrivacyIcon}
              detail={detail}
              accordions={accordions}
              isLoading={false}
              action={renderActions()}
              minorButton={
                type === PRIVACY_TYPE.MAJOR && (
                  <Box
                    sx={{
                      pt: '19px',
                      width: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'center',
                      alignItems: 'center',
                      textAlign: 'center'
                    }}
                  >
                    <Button
                      onClick={() => {
                        setIsLoading(true)
                        navigate({
                          pathname: '/policies/privacy-notices/latest',
                          search: minorSearch
                        })
                      }}
                      sx={{
                        minWidth: '230px',
                        fontSize: '13px',
                        lineHeight: '19px',
                        minHeight: '33px',
                        textTransform: 'none',
                        color: 'text.primary',
                        border: '1px solid #8d8d8d',
                        borderRadius: '24px',
                        textDecoration: 'none',
                        fontWeight: '500'
                      }}
                      data-testid='ButtonE34B2F70'
                    >
                      {t('iam.01.Common.button.Easy.to.understand.Privacy.Notice')}
                    </Button>
                  </Box>
                )
              }
            />
          </Box>
        </Box>
      )}
    </Box>
  )
}
